import COS, { onProgress } from 'cos-js-sdk-v5'
import FileSaver from 'file-saver'
import mime from 'mime'
import { commonUtils } from './commonUtils'

export type TokenData = {
  TmpSecretId: string
  TmpSecretKey: string
  Token: string
  startTime: number
  expiredTime: number
  Bucket: string
  Region: string
}

export type TypeFileResult = {
  key: string
  fileHash: string
  fileSize: number
  fileType: string
  fileName: string
}

export abstract class FileHelper {
  private _cacheCos: Record<'upload' | 'download', COS | null> = {
    upload: null,
    download: null,
  }

  private _tokenCache: Record<'upload' | 'download', TokenData | null> = {
    upload: null,
    download: null,
  }

  private _tokenRequestLock: Record<'upload' | 'download', Promise<TokenData | null> | null> = {
    upload: null,
    download: null,
  }

  public region: string = 'ap-guangzhou'
  public bucket: string = 'byy-1312430304'

  constructor(
    public prefix: string,
    bucket?: string,
    region?: string
  ) {
    this.bucket = bucket || this.bucket
    this.region = region || this.region
  }

  /** 获取cos实例 */
  private getCos(type: 'upload' | 'download', key?: string): COS {
    if (!this._cacheCos[type]) {
      this._cacheCos[type] = new COS({
        getAuthorization: async (options, callback) => {
          const cachedToken = this._tokenCache[type]
          // 提前3分钟更新，避免过期
          if (cachedToken && Date.now() < cachedToken.expiredTime * 1000 - 3 * 1000 * 60) {
            callback({
              TmpSecretId: cachedToken.TmpSecretId,
              TmpSecretKey: cachedToken.TmpSecretKey,
              SecurityToken: cachedToken.Token,
              StartTime: cachedToken.startTime,
              ExpiredTime: cachedToken.expiredTime,
            })
            return
          }

          // 如果没有缓存或已过期，检查是否有正在进行的请求
          if (this._tokenRequestLock[type]) {
            const data = await this._tokenRequestLock[type]
            if (data) {
              callback({
                TmpSecretId: data.TmpSecretId,
                TmpSecretKey: data.TmpSecretKey,
                SecurityToken: data.Token,
                StartTime: data.startTime,
                ExpiredTime: data.expiredTime,
              })
              return
            }
          }

          // 创建新的请求锁
          this._tokenRequestLock[type] = (async () => {
            try {
              const data = type === 'upload' ? await this.getUploadToken() : await this.getDownloadToken('*')
              if (!data) {
                console.error('获取 COS 临时密钥失败')
                return {
                  TmpSecretId: '',
                  TmpSecretKey: '',
                  Token: '',
                  startTime: 0,
                  expiredTime: 0,
                  Bucket: '',
                  Region: '',
                }
              }

              this._tokenCache[type] = data
              return data
            } finally {
              this._tokenRequestLock[type] = null
            }
          })()

          // 等待请求完成并回调
          const data = await this._tokenRequestLock[type]
          if (data) {
            callback({
              TmpSecretId: data.TmpSecretId,
              TmpSecretKey: data.TmpSecretKey,
              SecurityToken: data.Token,
              StartTime: data.startTime,
              ExpiredTime: data.expiredTime,
            })
          }
        },
      })
    }
    return this._cacheCos[type]!
  }

  /** 获取文件 hash、ext */
  static async getFileInfo(file: File) {
    const fileMd5 = await commonUtils.getFileMd5(file)
    let extension: string | undefined
    try {
      extension = mime.getExtension?.(file.type) || undefined
    } catch {
      extension = undefined
    }
    // 根据文件类型获取实际后缀，获取不到时在取文件名里
    if (!extension) {
      const parts = file.name?.split('.')
      extension = parts && parts.length > 1 ? parts[parts.length - 1] : undefined
    }
    const ext = extension ? `.${extension}` : ''
    return { fileMd5, ext, extension: extension || '' }
  }

  /** 上传文件 */
  async upload(file: File, onProgress?: onProgress): Promise<TypeFileResult | undefined> {
    const cos = this.getCos('upload')
    if (!cos) return

    const { fileMd5, ext, extension } = await FileHelper.getFileInfo(file)
    if (!fileMd5) return

    const Key = `${this.prefix}/${fileMd5}${ext}`

    const result = await cos.uploadFile({
      Bucket: this.bucket,
      Region: this.region,
      Key,
      Body: file,
      onProgress,
    })

    if (result.statusCode !== 200) return

    return {
      key: Key,
      fileHash: fileMd5,
      fileSize: file.size,
      fileType: extension,
      fileName: file.name,
    }
  }

  /** 批量上传文件 */
  async batchUpload(files: File[], onFileFinish: (data: TypeFileResult & { success: boolean }) => void) {
    const cos = this.getCos('upload')
    if (!cos) return

    return await Promise.all(
      files.map(async (item) => {
        const { fileMd5, ext, extension } = await FileHelper.getFileInfo(item)
        if (!fileMd5) return { success: false }
        const Key = `${this.prefix}/${fileMd5}${ext}`

        const fileInfo: TypeFileResult = {
          key: Key,
          fileHash: fileMd5,
          fileSize: item.size,
          fileType: extension,
          fileName: item.name,
        }

        const result = await cos.uploadFile({
          Bucket: this.bucket,
          Region: this.region,
          Key,
          Body: item,
        })

        return onFileFinish({
          ...fileInfo,
          success: result.statusCode === 200,
        })
      })
    )
  }

  /** 下载文件 */
  async download(key: string, filename?: string) {
    const downloadUrl = await this.getDownloadUrl(key, filename)
    if (!downloadUrl) return
    FileSaver.saveAs(downloadUrl, filename)
  }

  /** 获取下载地址 */
  async getDownloadUrl(key: string, filename?: string) {
    return new Promise<string | undefined>((resolve, reject) => {
      const cos = this.getCos('download', key)
      if (!cos) return resolve(undefined)

      cos.getObjectUrl(
        {
          Bucket: this.bucket,
          Region: this.region,
          Key: key,
          Sign: true,
        },
        function (err, data) {
          if (err) return console.log(err)
          var downloadUrl =
            data.Url + (data.Url.indexOf('?') > -1 ? '&' : '?') + 'response-content-disposition=attachment' // 补充强制下载的参数
          downloadUrl += `;filename="${filename}"`
          resolve(downloadUrl)
        }
      )
    })
  }

  /** 获取上传令牌 */
  abstract getUploadToken(): Promise<TokenData | null>

  /** 获取下载令牌 */
  abstract getDownloadToken(key: string): Promise<TokenData | null>
}
